import { Box, Button, Chip, FormControl, InputLabel, MenuItem, Select, Skeleton } from '@mui/material';
import { DrawerHeader } from '../../../../../drawer/DrawerHeader';
import { graphql } from '../../../../../gql';
import { useApiMutation, useApiQuery } from '../../../../../query/hooks';
import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';
import { CONTROL_CYCLE_OPTIONS } from './consts';
import { mutationResponseHandler } from '../../../../../utils/mutationResponseHandler';
import { ControlCheckHistoryTable } from './ControlCheckHistoryTable';
import { DrawerWrapper } from '../../../../../drawer/DrawerWrapper';
import { useAuthSignedIn } from '../../../../../auth/useAuth';
import { useSnackbar } from '../../../../../store/snackbar';

const activityOwnerQuery = graphql(`
	query journeyDetail_getActivityOwner($activityId: UUID!) {
		currentOwner: getActivity(by: { id: $activityId }) {
			controlCycle {
				id
				days
			}
			owner {
				id
				name
			}
		}
		selectOptions: listMember {
			id
			name
		}
	}
`);

const updateOwnerMutation = graphql(`
	mutation journeyDetail_updateOwnerMutation($activityId: UUID!, $ownerData: ActivityUpdateOwnerEntityRelationInput!) {
		updateActivity(by: { id: $activityId }, data: { owner: $ownerData }) {
			ok
			errorMessage
		}
	}
`);

const updateControlCycleDurationMutation = graphql(`
	mutation journeyDetail_updateControlCycle($activityId: UUID!, $controlCycle: ActivityUpdateControlCycleEntityRelationInput!) {
		updateActivity(by: { id: $activityId }, data: { controlCycle: $controlCycle }) {
			ok
			errorMessage
		}
	}
`);

const createControlCycleCheckMutation = graphql(`
	mutation journeyDetail_createControlCycleCheck($controlCycleId: UUID!, $userId: UUID!) {
		createControlCycleCheck(data: { cycle: { connect: { id: $controlCycleId } }, who: { connect: { id: $userId } } }) {
			ok
			errorMessage
		}
	}
`);

export const ownerEditDrawerSchema = z.object({
	activityId: z.string(),
});

export const OwnerEditDrawer: FC<z.infer<typeof ownerEditDrawerSchema>> = ({ activityId }) => {
	const intl = useIntl();
	const { me } = useAuthSignedIn();
	const { openSnackbar } = useSnackbar();

	const { data: ownerData, isLoading } = useApiQuery(activityOwnerQuery, {
		variables: { activityId },
	});

	const { mutate: updateOwner } = useApiMutation(updateOwnerMutation, {
		onSuccess: (result) =>
			mutationResponseHandler({
				result: result.updateActivity.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'widget.owner.update.success' }),
					error: intl.formatMessage({ id: 'widget.owner.update.error' }),
				},
				errorMessage: result.updateActivity.errorMessage,
				openSnackbar,
			}),
	});

	const { mutate: createControlCycleHistory } = useApiMutation(createControlCycleCheckMutation, {
		onSuccess: (result) =>
			mutationResponseHandler({
				result: result.createControlCycleCheck.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'widget.owner.control-cycle-check.success' }),
					error: intl.formatMessage({ id: 'widget.owner.control-cycle-check.error' }),
				},
				errorMessage: result.createControlCycleCheck.errorMessage,
				openSnackbar,
			}),
	});

	const { mutate: mutateControlCycleDuration } = useApiMutation(updateControlCycleDurationMutation, {
		onSuccess: (result) =>
			mutationResponseHandler({
				result: result.updateActivity.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'widget.owner.control-cycle.success' }),
					error: intl.formatMessage({ id: 'widget.owner.control-cycle.error' }),
				},
				errorMessage: result.updateActivity.errorMessage,
				openSnackbar,
			}),
	});

	const handleUpdateOwner = (newOwnerId: string) => {
		if (newOwnerId === '') {
			updateOwner({ activityId, ownerData: { disconnect: true } });
		} else if (newOwnerId !== ownerData?.currentOwner?.owner?.id) {
			updateOwner({ activityId, ownerData: { connect: { id: newOwnerId } } });
		}
	};

	const handleUpdateControlCycle = () => {
		if (me.id && ownerData?.currentOwner?.controlCycle?.id) {
			createControlCycleHistory({
				controlCycleId: ownerData.currentOwner.controlCycle.id,
				userId: me.id,
			});
		}
	};

	const handleUpdateControlCycleDuration = (duration: number | null) => {
		if (duration === 0 || !duration) {
			mutateControlCycleDuration({ activityId, controlCycle: { delete: true } });
		} else {
			// When new control cycle is created, add first history entry as well
			const conditionalData =
				!ownerData?.currentOwner?.controlCycle && me.id
					? {
							history: {
								create: {
									who: {
										connect: {
											id: me.id,
										},
									},
								},
							},
						}
					: {};
			mutateControlCycleDuration({
				activityId,
				controlCycle: {
					[ownerData?.currentOwner?.controlCycle ? 'update' : 'create']: {
						days: duration,
						...conditionalData,
					},
				},
			});
		}
	};

	return (
		<DrawerWrapper>
			<DrawerHeader title={isLoading ? <Skeleton /> : intl.formatMessage({ id: 'journey.detail.widget.owner.title' })} />
			{isLoading && <Skeleton animation={'wave'} width={'100%'} height={50} />}
			{isLoading && <Skeleton animation={'wave'} width={'100%'} height={50} />}
			{!isLoading && (
				<Select
					fullWidth
					value={ownerData?.currentOwner?.owner?.id || ''}
					renderValue={(ownerId) => {
						const owner = ownerData?.selectOptions.find((f) => f.id === ownerId);
						return (
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
								{owner ? <Chip key={owner.id} label={owner.name} variant={'outlined'} /> : null}
							</Box>
						);
					}}
					onChange={({ target: { value } }) => handleUpdateOwner(value)}
				>
					<MenuItem value={''}>
						<FormattedMessage id={'widget.owner.empty'} />
					</MenuItem>
					{ownerData?.selectOptions.map(({ id, name }) => (
						<MenuItem key={id} value={id}>
							{name}
						</MenuItem>
					))}
				</Select>
			)}
			{!isLoading && (
				<FormControl fullWidth>
					<InputLabel id={'control-cycle-label'}>
						<FormattedMessage id={'journey.detail.widget.owner.label.control-cycle'} />
					</InputLabel>
					<Select
						sx={{ flex: 1 }}
						value={ownerData?.currentOwner?.controlCycle?.days || 0}
						onChange={({ target: { value } }) => handleUpdateControlCycleDuration(value as number | null)}
						labelId={'control-cycle-label'}
					>
						{/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
						<MenuItem value={0}>-</MenuItem>
						{Object.entries(CONTROL_CYCLE_OPTIONS).map(([key, value]) => (
							<MenuItem value={Number(key)} key={key}>
								{value}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			)}
			<Button
				variant={'contained'}
				disabled={!ownerData?.currentOwner?.controlCycle?.days}
				fullWidth
				size={'large'}
				onClick={() => handleUpdateControlCycle()}
			>
				<FormattedMessage id={'journey.detail.widget.owner.check'} />
			</Button>
			<ControlCheckHistoryTable activityId={activityId} />
		</DrawerWrapper>
	);
};
